import React, { Suspense, lazy } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import { getRouteApi } from '@tanstack/react-router';
import ContentView from '~/components/ContentView';
import DrawerButton from '~/containers/DrawerButton';
import { useChangeSettings } from '~/hooks/api/useChangeSettings';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import messages from './messages';

const SettingsPlanningForm = lazy(() => import('./SettingsPlanningForm'));

const SettingsPlanningScheduleForm = lazy(
  () => import('./SettingsPlanningScheduleForm')
);

const SettingsNewsForm = lazy(() => import('./SettingsNewsForm'));

const SettingsReportingForm = lazy(() => import('./SettingsReportingForm'));

const SettingsProjectForm = lazy(() => import('./SettingsProjectForm'));

const SettingsParticipationForm = lazy(
  () => import('./SettingsParticipationForm')
);

const SettingsSmtpForm = lazy(() => import('./SettingsSmtpForm'));

const SettingsMasterDataForm = lazy(() => import('./SettingsMasterDataForm'));

const route = getRouteApi('/settings/$category');

const settingsMap: Record<
  string,
  {
    formName: string;
    component: React.LazyExoticComponent<
      (props: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        initialValues: any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSubmit: (values: any) => void;
      }) => React.JSX.Element
    >;
    titleMessage: MessageDescriptor;
  }
> = {
  planning: {
    formName: 'SettingsPlanningForm',
    component: SettingsPlanningForm,
    titleMessage: messages.planning,
  },
  planningSchedule: {
    formName: 'SettingsPlanningScheduleForm',
    component: SettingsPlanningScheduleForm,
    titleMessage: messages.planningSchedule,
  },
  news: {
    formName: 'SettingsNewsForm',
    component: SettingsNewsForm,
    titleMessage: messages.news,
  },
  reporting: {
    formName: 'SettingsReportingForm',
    component: SettingsReportingForm,
    titleMessage: messages.reporting,
  },
  'project-settings': {
    formName: 'SettingsProjectForm',
    component: SettingsProjectForm,
    titleMessage: messages.project,
  },
  participation: {
    formName: 'SettingsParticipationForm',
    component: SettingsParticipationForm,
    titleMessage: messages.participation,
  },
  smtp: {
    formName: 'SettingsSmtpForm',
    component: SettingsSmtpForm,
    titleMessage: messages.smtp,
  },
  masterData: {
    formName: 'SettingsMasterDataForm',
    component: SettingsMasterDataForm,
    titleMessage: messages.masterData,
  },
};

export const Settings = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const { category } = route.useParams();

  const { data: settings, isLoading: isFetchingSettings } =
    useSettingsByCategory(category);
  const settingsMutation = useChangeSettings({
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.editSettingsSuccess),
      });
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.editSettingsFailure),
      });
    },
  });
  const { component: SettingsForm, titleMessage } =
    settingsMap[category as keyof typeof settings];

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <DrawerButton />
            <Typography variant="h6" sx={{ ml: 2 }}>
              <FormattedMessage {...titleMessage} />
            </Typography>
          </Toolbar>
        </AppBar>
      }
      isLoading={isFetchingSettings}
    >
      {!isFetchingSettings && settings && (
        <Suspense>
          <SettingsForm
            onSubmit={(values: Record<string, unknown>) =>
              settingsMutation.mutate({
                categoryKey: category,
                settings: values,
              })
            }
            initialValues={settings}
          />
        </Suspense>
      )}
    </ContentView>
  );
};

export default Settings;
